import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import CrmImportPanel from './CrmImportPanel';
import LeadSourcingPanel from './LeadSourcingPanel';
import LeadSavedViews from './LeadSavedViews';
import './LeadsWorkspaceV2.css';
import './LeadCampaignFit.css';

function connectionLabel(value) {
  if (value === true) return 'Verbonden';
  if (value === false) return 'Niet verbonden';
  return 'Onbekend';
}

function nameOf(lead) {
  return lead.fullName || lead.leadName || [lead.firstName, lead.lastName].filter(Boolean).join(' ') || 'Naamloos';
}

function providerName(provider) {
  if (provider === 'hubspot') return 'HubSpot';
  if (provider === 'salesforce') return 'Salesforce';
  if (provider === 'dynamics') return 'Dynamics 365';
  return provider || 'CRM';
}

function crmStage(mapping) {
  return mapping?.metadata?.lifecycleStage || mapping?.metadata?.leadStatus || mapping?.metadata?.stage || '';
}

function crmLabel(mapping) {
  if (!mapping) return 'Niet gekoppeld';
  const provider = providerName(mapping.provider);
  if (mapping.syncStatus === 'error') return `${provider} · fout`;
  if (mapping.syncStatus === 'synced') return `${provider} · synced`;
  return `${provider} · gekoppeld`;
}

function crmDateLabel(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('nl-NL');
}

function fitLabel(value) {
  if (value === 'strong') return 'Sterk';
  if (value === 'medium') return 'Redelijk';
  if (value === 'weak') return 'Zwak';
  if (value === 'unknown') return 'Onbekend';
  return 'Niet geëvalueerd';
}

function LeadsWorkspaceV2() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, connected: 0, replied: 0, withEmail: 0, withLinkedIn: 0, crmLinked: 0, crmProviders: {} });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 25 });
  const [options, setOptions] = useState({ campaigns: [], fitOptions: [] });
  const [fitStatus, setFitStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fitBusy, setFitBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showCrmImport, setShowCrmImport] = useState(searchParams.get('crm') === 'hubspot');
  const [showSourcing, setShowSourcing] = useState(searchParams.get('source') === 'goal');

  const params = useMemo(() => ({
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 25),
    search: searchParams.get('search') || '',
    accepted: searchParams.get('accepted') || '',
    replied: searchParams.get('replied') || '',
    hasEmail: searchParams.get('hasEmail') || '',
    crmProvider: searchParams.get('crmProvider') || '',
    crmSyncStatus: searchParams.get('crmSyncStatus') || '',
    crmOpenOpportunity: searchParams.get('crmOpenOpportunity') || '',
    crmDoNotContact: searchParams.get('crmDoNotContact') || '',
    crmRecentActivityDays: searchParams.get('crmRecentActivityDays') || '',
    campaignId: searchParams.get('campaignId') || '',
    campaignFit: searchParams.get('campaignFit') || '',
    fitMinScore: searchParams.get('fitMinScore') || '',
    fitMaxScore: searchParams.get('fitMaxScore') || '',
    sortBy: searchParams.get('sortBy') || 'updatedAt',
    sortDir: searchParams.get('sortDir') || 'desc',
  }), [searchParams]);

  const setParam = (key, value, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    if (value === '' || value == null) next.delete(key); else next.set(key, String(value));
    if (key === 'campaignId' && !value) {
      next.delete('campaignFit');
      next.delete('fitMinScore');
      next.delete('fitMaxScore');
    }
    if (resetPage && key !== 'page') next.set('page', '1');
    setSearchParams(next);
  };

  const loadOptions = useCallback(async () => {
    try {
      const response = await api.get('/leads/workspace/options');
      setOptions(response.data || { campaigns: [], fitOptions: [] });
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    }
  }, []);

  const loadFitStatus = useCallback(async () => {
    if (!params.campaignId) {
      setFitStatus(null);
      return;
    }
    try {
      const response = await api.get(`/leads/campaign-fit/${params.campaignId}`);
      setFitStatus(response.data || null);
    } catch (requestError) {
      setFitStatus(null);
      if (requestError.response?.status !== 404) setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    }
  }, [params.campaignId]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const query = Object.fromEntries(Object.entries(params).filter(([, value]) => value !== ''));
      const [workspaceResponse, statsResponse] = await Promise.all([
        api.get('/leads/workspace', { params: query }),
        api.get('/leads/workspace/stats'),
      ]);
      setRows(workspaceResponse.data.items || []);
      setPagination(workspaceResponse.data.pagination || { page: 1, pages: 1, total: 0, limit: params.limit });
      setStats(statsResponse.data || {});
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { loadOptions(); }, [loadOptions]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFitStatus(); }, [loadFitStatus]);

  const evaluateCampaignFit = async () => {
    if (!params.campaignId) return;
    setFitBusy(true); setError(''); setNotice('');
    try {
      const response = await api.post('/leads/campaign-fit/evaluate', { campaignId: params.campaignId });
      const summary = response.data?.summary || {};
      setNotice(`${response.data?.evaluated || 0} leads opnieuw geëvalueerd: ${summary.strong || 0} sterk, ${summary.medium || 0} redelijk, ${summary.weak || 0} zwak.`);
      await Promise.all([load(), loadFitStatus()]);
    } catch (requestError) {
      const code = requestError.response?.data?.error;
      if (code === 'CAMPAIGN_SOURCING_BRIEF_REQUIRED') {
        setError('Deze campagne heeft nog geen goedgekeurde doelgroepbrief. Maak of keur eerst een doelgroepbrief goed via “Vind leads voor een doel”.');
      } else {
        setError(requestError.response?.data?.message || code || requestError.message);
      }
    } finally { setFitBusy(false); }
  };

  const closeModalParam = (key) => {
    const next = new URLSearchParams(searchParams);
    next.delete(key);
    setSearchParams(next, { replace: true });
  };
  const crmFiltered = Boolean(params.crmProvider || params.crmSyncStatus || params.crmOpenOpportunity || params.crmDoNotContact || params.crmRecentActivityDays);
  const fitFiltered = Boolean(params.campaignFit || params.fitMinScore || params.fitMaxScore);
  const selectedCampaign = options.campaigns?.find((campaign) => campaign.id === params.campaignId);

  return <main className="leads-v2-page">
    <header className="leads-v2-header">
      <div>
        <p className="leads-v2-kicker">Lead intelligence</p>
        <h1>Leads</h1>
        <p>Zoek, filter en prioriteer leads voor campagnes, AI-agent en CRM.</p>
      </div>
      <div className="leads-v2-header-actions">
        <button className="leads-v2-button" onClick={() => setShowSourcing(true)}>Vind leads voor een doel</button>
        <button className="leads-v2-button secondary" onClick={() => setShowCrmImport(true)}>Importeer uit CRM</button>
        <Link className="leads-v2-button secondary" to="/leads/legacy">LinkedIn, CSV & lijsten</Link>
        <button className="leads-v2-button secondary" onClick={load}>Vernieuwen</button>
      </div>
    </header>

    {notice && <div className="leads-v2-notice">{notice}</div>}
    {error && <div className="leads-v2-error">{error}</div>}

    <section className="leads-v2-kpis leads-v2-kpis-six">
      <div><span>Totaal</span><strong>{stats.total || 0}</strong></div>
      <div><span>LinkedIn verbonden</span><strong>{stats.connected || 0}</strong></div>
      <div><span>Gereageerd</span><strong>{stats.replied || 0}</strong></div>
      <div><span>Met e-mail</span><strong>{stats.withEmail || 0}</strong></div>
      <div><span>Met LinkedIn</span><strong>{stats.withLinkedIn || 0}</strong></div>
      <div><span>CRM gekoppeld</span><strong>{stats.crmLinked || 0}</strong><small>{stats.crmProviders?.hubspot?.linked ? `${stats.crmProviders.hubspot.linked} HubSpot` : ''}</small></div>
    </section>

    <LeadSavedViews onNotice={setNotice} onError={setError} />

    {params.campaignId && <section className="leads-v2-campaign-fit-bar">
      <div>
        <span>Campagne-fit</span>
        <strong>{selectedCampaign?.title || 'Geselecteerde campagne'}</strong>
        <small>{fitStatus?.evaluated || 0} leads geëvalueerd{fitStatus?.lastEvaluatedAt ? ` · laatst ${new Date(fitStatus.lastEvaluatedAt).toLocaleString()}` : ''}</small>
      </div>
      <div className="leads-v2-campaign-fit-summary">
        <span><b>{fitStatus?.summary?.strong || 0}</b> sterk</span>
        <span><b>{fitStatus?.summary?.medium || 0}</b> redelijk</span>
        <span><b>{fitStatus?.summary?.weak || 0}</b> zwak</span>
        <button className="leads-v2-button secondary" disabled={fitBusy} onClick={evaluateCampaignFit}>{fitBusy ? 'Evalueren…' : 'Fit opnieuw berekenen'}</button>
      </div>
    </section>}

    <section className="leads-v2-toolbar">
      <label className="leads-v2-search">
        <span>Zoeken</span>
        <input value={params.search} onChange={(event) => setParam('search', event.target.value)} placeholder="Naam, e-mail, bedrijf of LinkedIn URL" />
      </label>
      <label><span>Campagne</span><select value={params.campaignId} onChange={(event) => setParam('campaignId', event.target.value)}><option value="">Geen campagne-fit</option>{(options.campaigns || []).map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.title}</option>)}</select></label>
      <label><span>Fit</span><select value={params.campaignFit} disabled={!params.campaignId} onChange={(event) => setParam('campaignFit', event.target.value)}><option value="">Alle fits</option><option value="strong">Sterk</option><option value="medium">Redelijk</option><option value="weak">Zwak</option><option value="unknown">Onbekend</option></select></label>
      <label><span>Min. fit-score</span><input type="number" min="0" max="100" disabled={!params.campaignId} value={params.fitMinScore} onChange={(event) => setParam('fitMinScore', event.target.value)} placeholder="0" /></label>
      <label><span>LinkedIn</span><select value={params.accepted} onChange={(event) => setParam('accepted', event.target.value)}><option value="">Alle statussen</option><option value="connected">Verbonden</option><option value="not_connected">Niet verbonden</option><option value="unknown">Onbekend</option></select></label>
      <label><span>Reply</span><select value={params.replied} onChange={(event) => setParam('replied', event.target.value)}><option value="">Alle</option><option value="true">Gereageerd</option><option value="false">Niet gereageerd</option></select></label>
      <label><span>E-mail</span><select value={params.hasEmail} onChange={(event) => setParam('hasEmail', event.target.value)}><option value="">Alle</option><option value="true">E-mail bekend</option><option value="false">Geen e-mail</option></select></label>
      <label><span>CRM</span><select value={params.crmProvider} onChange={(event) => setParam('crmProvider', event.target.value)}><option value="">Alle leads</option><option value="hubspot">HubSpot</option><option value="salesforce">Salesforce</option><option value="dynamics">Dynamics 365</option></select></label>
      <label><span>CRM sync</span><select value={params.crmSyncStatus} onChange={(event) => setParam('crmSyncStatus', event.target.value)}><option value="">Alle</option><option value="linked">Gekoppeld</option><option value="imported">Geïmporteerd</option><option value="synced">Gesynchroniseerd</option><option value="error">Syncfout</option></select></label>
      <label><span>Opportunity</span><select value={params.crmOpenOpportunity} onChange={(event) => setParam('crmOpenOpportunity', event.target.value)}><option value="">Alle</option><option value="true">Open opportunity</option><option value="false">Geen open opportunity</option></select></label>
      <label><span>CRM contact</span><select value={params.crmDoNotContact} onChange={(event) => setParam('crmDoNotContact', event.target.value)}><option value="">Alle</option><option value="true">Contact geblokkeerd</option><option value="false">Contact toegestaan</option></select></label>
      <label><span>CRM activiteit</span><select value={params.crmRecentActivityDays} onChange={(event) => setParam('crmRecentActivityDays', event.target.value)}><option value="">Alle</option><option value="7">Laatste 7 dagen</option><option value="14">Laatste 14 dagen</option><option value="30">Laatste 30 dagen</option><option value="90">Laatste 90 dagen</option></select></label>
      <label><span>Sortering</span><select value={`${params.sortBy}:${params.sortDir}`} onChange={(event) => { const [sortBy, sortDir] = event.target.value.split(':'); const next = new URLSearchParams(searchParams); next.set('sortBy', sortBy); next.set('sortDir', sortDir); next.set('page', '1'); setSearchParams(next); }}><option value="updatedAt:desc">Laatst bijgewerkt</option><option value="createdAt:desc">Nieuwste</option><option value="firstName:asc">Voornaam A-Z</option><option value="lastName:asc">Achternaam A-Z</option></select></label>
      {(crmFiltered || fitFiltered) && <button className="leads-v2-filter-reset" onClick={() => { const next = new URLSearchParams(searchParams); ['crmProvider','crmSyncStatus','crmOpenOpportunity','crmDoNotContact','crmRecentActivityDays','campaignFit','fitMinScore','fitMaxScore'].forEach((key) => next.delete(key)); next.set('page', '1'); setSearchParams(next); }}>Extra filters wissen</button>}
    </section>

    <section className="leads-v2-table-card">
      <div className="leads-v2-table-summary"><strong>{pagination.total || 0} leads</strong><span>Pagina {pagination.page || 1} van {pagination.pages || 1}</span></div>
      <div className="leads-v2-table-wrap">
        <table className="leads-v2-table">
          <thead><tr><th>Lead</th><th>Bedrijf</th><th>Contact</th>{params.campaignId && <th>Fit</th>}<th>LinkedIn</th><th>Reply</th><th>CRM</th><th>Lijsten</th><th>Bijgewerkt</th></tr></thead>
          <tbody>
            {!loading && rows.map((lead) => { const crm = lead.crmMappings?.[0]; const context = lead.crmContext || {}; const stage = context.lifecycleStage || context.leadStatus || crmStage(crm); const fit = lead.campaignFit; return <tr key={lead._id} onClick={() => navigate(`/leads/${lead._id}`, { state: { fromWorkspace: `${window.location.pathname}${window.location.search}` } })}>
              <td><div className="leads-v2-person"><div className="leads-v2-avatar">{nameOf(lead).slice(0, 1).toUpperCase()}</div><div><strong>{nameOf(lead)}</strong><small>{lead.title || lead.jobTitle || ''}</small></div></div></td>
              <td><strong>{lead.organization || lead.company || '—'}</strong></td>
              <td><span>{lead.email || 'Geen e-mail'}</span></td>
              {params.campaignId && <td><div className={`leads-v2-campaign-fit-cell ${fit?.fit || 'unknown'}`}><strong>{fit ? fit.score : '—'}</strong><span>{fitLabel(fit?.fit)}</span>{fit?.reasons?.[0] && <small>{fit.reasons[0]}</small>}</div></td>}
              <td><span className={`leads-v2-pill ${lead.accepted === true ? 'success' : lead.accepted === false ? 'neutral' : 'muted'}`}>{connectionLabel(lead.accepted)}</span></td>
              <td><span className={`leads-v2-pill ${lead.replied ? 'success' : 'muted'}`}>{lead.replied ? 'Gereageerd' : 'Geen reply'}</span></td>
              <td><div className="leads-v2-crm-cell"><span className={`leads-v2-pill ${context.syncError || crm?.syncStatus === 'error' ? 'error' : crm ? 'info' : 'muted'}`}>{crmLabel(crm)}</span>{stage && <small>{stage}</small>}{context.openOpportunity === true && <small>Open opportunity</small>}{context.doNotContact === true && <small className="error">Contact geblokkeerd in CRM</small>}{context.lastCrmActivityAt && <small>Laatste CRM-activiteit: {crmDateLabel(context.lastCrmActivityAt)}</small>}{context.lastWritebackEventType && <small>Laatste Leadgenflow-sync: {context.lastWritebackEventType}</small>}{crm?.lastError?.message && <small className="error">{crm.lastError.message}</small>}</div></td>
              <td><div className="leads-v2-list-tags">{(lead.leadLists || []).slice(0, 2).map((list) => <span key={`${lead._id}-${list.id || list.title}`}>{list.title || list.leadListTitle}</span>)}{(lead.leadLists || []).length > 2 && <small>+{lead.leadLists.length - 2}</small>}</div></td>
              <td>{lead.updatedAt ? new Date(lead.updatedAt).toLocaleDateString() : '—'}</td>
            </tr>; })}
            {loading && <tr><td colSpan={params.campaignId ? 9 : 8} className="leads-v2-empty">Leads laden…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={params.campaignId ? 9 : 8} className="leads-v2-empty">Geen leads gevonden met deze filters.</td></tr>}
          </tbody>
        </table>
      </div>
      <footer className="leads-v2-pagination">
        <label>Per pagina<select value={params.limit} onChange={(event) => setParam('limit', event.target.value)}><option value="25">25</option><option value="50">50</option><option value="100">100</option><option value="200">200</option></select></label>
        <div><button disabled={(pagination.page || 1) <= 1} onClick={() => setParam('page', (pagination.page || 1) - 1, false)}>Vorige</button><button disabled={(pagination.page || 1) >= (pagination.pages || 1)} onClick={() => setParam('page', (pagination.page || 1) + 1, false)}>Volgende</button></div>
      </footer>
    </section>

    {showCrmImport && <CrmImportPanel onClose={() => { setShowCrmImport(false); closeModalParam('crm'); }} onImported={(result) => { setNotice(`${result.created || 0} nieuwe leads geïmporteerd; ${result.linked || 0} bestaande leads aan de HubSpot-lijst gekoppeld.`); load(); }} />}
    {showSourcing && <LeadSourcingPanel onClose={() => { setShowSourcing(false); closeModalParam('source'); }} />}
  </main>;
}

export default LeadsWorkspaceV2;
